import type { ColoringTemplate, Story } from '@/contracts/content';
import {
  TEMPLATE_LEAF_DEN, TEMPLATE_MORNING_PUDDLE, TEMPLATE_PAPER_MOON,
  TEMPLATE_PIP_POND, TEMPLATE_RAIN_POND, TEMPLATE_SWAY,
  TEMPLATE_NIB_SHELL, TEMPLATE_DEW_LEAF, TEMPLATE_GARDEN_MORNING,
} from './templates';
import { getPaper, isPaper } from './papers';

/**
 * The free flagship story. Per spec §8.1 and §10.2, this one story must
 * demonstrate the COMPLETE transformation experience: read → explore →
 * create → bring to life → keep. Everything here is authored content with a
 * permanent ID per page, activity and template.
 */
export const PIP_AND_THE_PAPER_MOON: Story = {
  id: 'story_pip_paper_moon',
  slug: 'pip-and-the-paper-moon',
  title: 'Pip and the Paper Moon',
  collection: 'Night Garden',
  synopsis:
    'The moon goes missing from the pond, and a small fox decides that if you cannot find a thing, you can always make one.',
  themes: ['Night-time', 'Friendship', 'Making things', 'Imagination'],
  characterIds: ['char_pip', 'char_sway'],
  coverSceneId: 'pip-cover',
  contentNotes: {
    vocabulary: 'Everyday words, with three repeated story words: pond, drift, glow.',
    sentenceLength: 'One or two short sentences per page.',
    supportNeeds: 'Works in all three reading modes. Narration is written for a slow, calm pace.',
  },
  conversationPrompt:
    'Pip made a new moon instead of giving up. Ask: “What would you have made?”',
  access: 'free',
  currentVersion: {
    versionId: 'v_pip_paper_moon_1',
    language: 'en',
    published: true,
    readMinutes: 4,
    listenMinutes: 5,
    pages: [
      {
        id: 'pg_pip_1',
        sequence: 0,
        text: 'Every night, Pip came down to the pond to say goodnight to the moon.',
        sceneId: 'pip-p1-goodnight',
        alt: 'A small orange fox sits on a dark pond bank, looking up at a round yellow moon.',
        narrationId: null,
      },
      {
        id: 'pg_pip_2',
        sequence: 1,
        text: 'But tonight, the sky was empty. The moon had drifted away.',
        sceneId: 'pip-p2-empty-sky',
        alt: 'The same pond, but the sky where the moon was is now empty and dark.',
        narrationId: null,
      },
      {
        id: 'pg_pip_3',
        sequence: 2,
        text: '“I saw it go,” whispered Sway, a moth with wings like little lanterns.',
        sceneId: 'pip-p3-sway',
        alt: 'A glowing teal and purple moth hovers above the fox, lighting the dark.',
        narrationId: null,
      },
      {
        id: 'pg_pip_4',
        sequence: 3,
        text: 'They looked in the reeds. They looked under the willow. No moon anywhere.',
        sceneId: 'pip-p4-searching',
        alt: 'The fox searches among tall reeds while the moth lights the way.',
        narrationId: null,
      },
      {
        id: 'pg_pip_5',
        sequence: 4,
        text: '“Then we will make one,” said Pip. And he fetched his paper.',
        sceneId: 'pip-p5-idea',
        alt: 'Inside a cosy burrow, the fox stands beside sheets of cream paper and a warm lantern.',
        narrationId: null,
      },
      {
        id: 'pg_pip_6',
        sequence: 5,
        text: 'They hung it high. And the pond glowed silver again.',
        sceneId: 'pip-p6-paper-moon',
        alt: 'A folded paper star-moon hangs glowing over the pond, with the fox and moth below.',
        narrationId: null,
      },
    ],
    activities: [
      {
        id: 'act_pip_missing',
        kind: 'picture-choice',
        prompt: 'What went missing from the sky?',
        options: [
          { id: 'opt_moon', label: 'The moon', sceneId: 'opt-moon' },
          { id: 'opt_sun', label: 'The sun', sceneId: 'opt-sun' },
          { id: 'opt_cloud', label: 'A cloud', sceneId: 'opt-cloud' },
        ],
        correctOptionId: 'opt_moon',
        hint: 'Let’s look again at the page where the sky went dark.',
        hintPageId: 'pg_pip_2',
      },
      {
        id: 'act_pip_order',
        kind: 'sequence',
        prompt: 'Put the story in order. What happened first?',
        options: [
          { id: 'seq_look', label: 'Pip looks for the moon', sceneId: 'opt-pip' },
          { id: 'seq_help', label: 'Sway comes to help', sceneId: 'opt-sway' },
          { id: 'seq_make', label: 'They make a paper moon', sceneId: 'opt-paper' },
        ],
        correctOrder: ['seq_look', 'seq_help', 'seq_make'],
        hint: 'Think about who Pip met before he fetched the paper.',
        hintPageId: 'pg_pip_3',
      },
      {
        id: 'act_pip_reflect',
        kind: 'reflection',
        // No correct answer. Nothing is scored (spec §7 C05).
        prompt: 'Pip made a new moon. What would you make out of paper?',
      },
    ],
    templates: [TEMPLATE_PIP_POND, TEMPLATE_SWAY, TEMPLATE_PAPER_MOON],

    /**
     * The three coloring pictures each come FROM a specific page, so in a
     * child's personal edition their picture stands in for the illustration on
     * that page. The published story is untouched (spec §10.1).
     */
    editionSlots: [
      {
        pageId: 'pg_pip_1',
        acceptsTemplateIds: [TEMPLATE_PIP_POND.id],
        caption: 'Pip and the pond, drawn by you',
      },
      {
        pageId: 'pg_pip_3',
        acceptsTemplateIds: [TEMPLATE_SWAY.id],
        caption: 'Sway’s lantern wings, drawn by you',
      },
      {
        pageId: 'pg_pip_6',
        acceptsTemplateIds: [TEMPLATE_PAPER_MOON.id],
        caption: 'The paper moon, drawn by you',
      },
    ],
    coverTemplateIds: [TEMPLATE_SWAY.id, TEMPLATE_PAPER_MOON.id, TEMPLATE_PIP_POND.id],
  },
};


/**
 * The second story. Deliberately different in shape from the first — five
 * pages instead of six, a different mix of activities, and only one of its
 * three pictures has a living scene. Authored entirely through the same
 * contract, with no story-specific code anywhere in the app.
 *
 * Pip and Sway recur, which is what makes the recurring-character promise in
 * F18 real rather than decorative.
 */
export const PIP_AND_THE_LOUDEST_NIGHT: Story = {
  id: 'story_pip_loudest_night',
  slug: 'pip-and-the-loudest-night',
  title: 'Pip and the Loudest Night',
  collection: 'Night Garden',
  synopsis:
    'A storm arrives over the pond, and Pip discovers that the loudest night is easier with somebody in it.',
  themes: ['Weather', 'Feeling scared', 'Friendship', 'Morning'],
  characterIds: ['char_pip', 'char_sway'],
  coverSceneId: 'storm-cover',
  contentNotes: {
    vocabulary: 'Simple weather words: wind, rain, thunder, drum, puddle.',
    sentenceLength: 'Short. Page two is a single sentence on purpose.',
    supportNeeds:
      'A calm read. Worth pausing on page two if a child finds storms frightening — the story resolves into comfort, not a scare.',
  },
  conversationPrompt:
    'Pip was frightened and Sway stayed with him. Ask: “What makes you feel cosy when it is loud outside?”',
  access: 'free',
  currentVersion: {
    versionId: 'v_pip_loudest_night_1',
    language: 'en',
    published: true,
    readMinutes: 3,
    listenMinutes: 4,
    pages: [
      {
        id: 'pg_storm_1',
        sequence: 0,
        text: 'The wind came first. It pulled at the willow and bent the reeds flat.',
        sceneId: 'storm-p1-wind',
        alt: 'A dark, windy pond. The willow tree leans over and the reeds bend.',
        narrationId: null,
      },
      {
        id: 'pg_storm_2',
        sequence: 1,
        text: 'Then came a sound so big that Pip’s ears folded flat.',
        sceneId: 'storm-p2-thunder',
        alt: 'Lightning splits the sky above the pond and the small fox flattens his ears.',
        narrationId: null,
      },
      {
        id: 'pg_storm_3',
        sequence: 2,
        text: '“Over here,” called Sway, from under the willow roots.',
        sceneId: 'storm-p3-sway-calls',
        alt: 'The glowing moth waits in a gap between the willow roots, calling to the fox.',
        narrationId: null,
      },
      {
        id: 'pg_storm_4',
        sequence: 3,
        text: 'They made a den of leaves, and listened to the rain drum on the roof.',
        sceneId: 'storm-p4-den',
        alt: 'The fox and the moth sit inside a rounded den built of big green leaves.',
        narrationId: null,
      },
      {
        id: 'pg_storm_5',
        sequence: 4,
        text: 'In the morning the storm had gone, and every puddle was full of sky.',
        sceneId: 'storm-p5-morning',
        alt: 'A bright morning. Wide puddles on the grass reflect the blue sky and the sun.',
        narrationId: null,
      },
    ],
    activities: [
      {
        id: 'act_storm_made',
        kind: 'picture-choice',
        prompt: 'What did Pip and Sway make?',
        options: [
          { id: 'opt_den', label: 'A den of leaves', sceneId: 'opt-den' },
          { id: 'opt_nest', label: 'A nest', sceneId: 'opt-nest' },
          { id: 'opt_boat', label: 'A boat', sceneId: 'opt-boat' },
        ],
        correctOptionId: 'opt_den',
        hint: 'Let’s look again at the page where they got out of the rain.',
        hintPageId: 'pg_storm_4',
      },
      {
        id: 'act_storm_weather',
        kind: 'picture-choice',
        prompt: 'What was the weather like in the morning?',
        options: [
          { id: 'opt_bright', label: 'Bright and sunny', sceneId: 'opt-sun' },
          { id: 'opt_still_raining', label: 'Still raining', sceneId: 'opt-rain' },
          { id: 'opt_still_scary', label: 'Still loud', sceneId: 'opt-scared' },
        ],
        correctOptionId: 'opt_bright',
        hint: 'The last page shows the morning. Shall we look?',
        hintPageId: 'pg_storm_5',
      },
      {
        id: 'act_storm_reflect',
        kind: 'reflection',
        prompt: 'Pip felt better once Sway was there. Who do you like to be near when something is loud?',
      },
    ],
    templates: [TEMPLATE_LEAF_DEN, TEMPLATE_RAIN_POND, TEMPLATE_MORNING_PUDDLE],
    editionSlots: [
      {
        pageId: 'pg_storm_4',
        acceptsTemplateIds: [TEMPLATE_LEAF_DEN.id],
        caption: 'The leaf den, drawn by you',
      },
      {
        pageId: 'pg_storm_2',
        acceptsTemplateIds: [TEMPLATE_RAIN_POND.id],
        caption: 'Rain on the pond, drawn by you',
      },
      {
        pageId: 'pg_storm_5',
        acceptsTemplateIds: [TEMPLATE_MORNING_PUDDLE.id],
        caption: 'A puddle full of sky, drawn by you',
      },
    ],
    coverTemplateIds: [TEMPLATE_MORNING_PUDDLE.id, TEMPLATE_LEAF_DEN.id, TEMPLATE_RAIN_POND.id],
  },
};

/**
 * Catalogue placeholders. These are shown on the public site as themes we are
 * building, never as available stories — `status: 'planned'` keeps the UI
 * honest (spec §5 W02–W05: state the actual catalogue size).
 */
/**
 * The third story, and the first daytime one.
 *
 * Structurally it is the odd one out on purpose. The first two are both
 * problem-and-solution: something goes wrong, and Pip makes something to put
 * it right. This one has no problem to solve. Pip meets someone slow, decides
 * to match their pace, and is rewarded with a garden he had been walking past.
 *
 * That matters beyond variety. A catalogue where every story teaches "fix it
 * by making something" is a catalogue with one idea in it, and the whole
 * premise here is looking closely — which is also what the product is named
 * after. It is also a harder shape for the app: the activities cannot lean on
 * a chain of cause and effect, and the third picture has no living scene, so
 * the create screen has to be honest about which pictures move.
 */
export const PIP_AND_THE_VERY_SMALL_VISITOR: Story = {
  id: 'story_pip_small_visitor',
  slug: 'pip-and-the-very-small-visitor',
  title: 'Pip and the Very Small Visitor',
  collection: 'Slow Mornings',
  synopsis:
    'The garden is busy and quick, until Pip meets someone who is not in a hurry at all — and finds out how much he had been walking past.',
  themes: ['Noticing', 'Patience', 'Friendship', 'Small things'],
  characterIds: ['char_pip', 'char_sway', 'char_nib'],
  coverSceneId: 'nib-cover',
  contentNotes: {
    vocabulary:
      'Everyday words, with three repeated story words: slow, still, here. “Mending” is introduced with a picture that explains it.',
    sentenceLength: 'One or two short sentences per page; the middle pages are shortest.',
    supportNeeds:
      'Works in all three reading modes. Narration is written with a pause after each page — the story is about waiting, and reading it quickly works against it.',
  },
  conversationPrompt:
    'Pip found things by staying still. Ask: “What did you notice today that you nearly missed?”',
  access: 'free',
  currentVersion: {
    versionId: 'v_pip_small_visitor_1',
    language: 'en',
    published: true,
    readMinutes: 4,
    listenMinutes: 5,
    pages: [
      {
        id: 'pg_nib_1',
        sequence: 0,
        text: 'The garden was busy. The bees were quick, and the wind was quicker.',
        sceneId: 'nib-p1-busy',
        alt: 'A bright morning garden with three bees darting between flowers while a small orange fox watches.',
        narrationId: null,
      },
      {
        id: 'pg_nib_2',
        sequence: 1,
        text: 'Then Pip found someone who was not in a hurry at all.',
        sceneId: 'nib-p2-found',
        alt: 'A snail with a spiral shell rests on a wide green leaf. The fox has stopped to look.',
        narrationId: null,
      },
      {
        id: 'pg_nib_3',
        sequence: 2,
        text: '“Where are you going?” asked Pip. “Here,” said the snail.',
        sceneId: 'nib-p3-where',
        alt: 'The fox has crouched down to the snail’s height so they are eye to eye.',
        narrationId: null,
      },
      {
        id: 'pg_nib_4',
        sequence: 3,
        text: 'So Pip stayed still. And the garden got slower, and bigger.',
        sceneId: 'nib-p4-waiting',
        alt: 'The fox sits quietly beside the leaf. A spider web and a small seed have appeared in the garden around them.',
        narrationId: null,
      },
      {
        id: 'pg_nib_5',
        sequence: 4,
        text: 'He saw a seed shaped like a boat. He saw a spider mending her thread.',
        sceneId: 'nib-p5-close',
        alt: 'A close view of the garden: a seed like a little boat, dew on a leaf, and a spider repairing one strand of her web.',
        narrationId: null,
      },
      {
        id: 'pg_nib_6',
        sequence: 5,
        text: '“You were right,” said Pip. “Here is a good place to go.”',
        sceneId: 'nib-p6-here',
        alt: 'The fox and the snail together on the leaf in the morning sun, with the moth arriving above them.',
        narrationId: null,
      },
    ],
    activities: [
      {
        id: 'act_nib_slow',
        kind: 'picture-choice',
        prompt: 'Who was not in a hurry?',
        options: [
          { id: 'opt_snail', label: 'The snail', sceneId: 'opt-snail' },
          { id: 'opt_bee', label: 'A bee', sceneId: 'opt-bee' },
          { id: 'opt_wind', label: 'The wind', sceneId: 'opt-wind' },
        ],
        correctOptionId: 'opt_snail',
        hint: 'Let’s look again at the page where Pip stopped walking.',
        hintPageId: 'pg_nib_2',
      },
      {
        id: 'act_nib_order',
        kind: 'sequence',
        prompt: 'Put the story in order. What happened first?',
        options: [
          { id: 'seq_found', label: 'Pip finds the snail', sceneId: 'opt-nib-found' },
          { id: 'seq_wait', label: 'Pip stays still', sceneId: 'opt-nib-wait' },
          { id: 'seq_see', label: 'Pip sees the small things', sceneId: 'opt-nib-see' },
        ],
        correctOrder: ['seq_found', 'seq_wait', 'seq_see'],
        hint: 'He had to stop before he could see anything.',
        hintPageId: 'pg_nib_4',
      },
      {
        id: 'act_nib_reflect',
        kind: 'reflection',
        // No correct answer, nothing recorded (spec §7 C05).
        prompt: 'Pip found small things by staying still. What would you look at for a long time?',
      },
    ],
    templates: [TEMPLATE_NIB_SHELL, TEMPLATE_DEW_LEAF, TEMPLATE_GARDEN_MORNING],

    editionSlots: [
      {
        pageId: 'pg_nib_2',
        acceptsTemplateIds: [TEMPLATE_NIB_SHELL.id],
        caption: 'Nib and his shell, drawn by you',
      },
      {
        pageId: 'pg_nib_5',
        acceptsTemplateIds: [TEMPLATE_DEW_LEAF.id],
        caption: 'The leaf that held the morning, drawn by you',
      },
      {
        pageId: 'pg_nib_1',
        acceptsTemplateIds: [TEMPLATE_GARDEN_MORNING.id],
        caption: 'The whole garden, drawn by you',
      },
    ],
    coverTemplateIds: [TEMPLATE_NIB_SHELL.id, TEMPLATE_GARDEN_MORNING.id, TEMPLATE_DEW_LEAF.id],
  },
};

export const PLANNED_COLLECTIONS = [
  {
    id: 'col_night_garden',
    title: 'Night Garden',
    description: 'Stories for the end of the day, with Pip the fox and Sway the moth.',
    sceneId: 'pip-cover',
    status: 'available' as const,
    storyCount: 2,
  },
  {
    id: 'col_slow_mornings',
    title: 'Slow Mornings',
    description: 'Daytime stories about looking closely at small things.',
    sceneId: 'nib-cover',
    status: 'available' as const,
    storyCount: 1,
  },
  {
    id: 'col_deep_wood',
    title: 'The Deep Wood',
    description: 'Forest friends, big weather, and finding your way back.',
    sceneId: 'story-forest',
    status: 'planned' as const,
    storyCount: 0,
  },
  {
    id: 'col_far_up',
    title: 'Far Up',
    description: 'Planets, rings, and the long quiet between stars.',
    sceneId: 'story-space',
    status: 'planned' as const,
    storyCount: 0,
  },
  {
    id: 'col_our_house',
    title: 'Our House',
    description: 'New siblings, first days, and the ordinary brave things.',
    sceneId: 'story-home',
    status: 'planned' as const,
    storyCount: 0,
  },
];

export const STORIES: Story[] = [
  PIP_AND_THE_PAPER_MOON,
  PIP_AND_THE_LOUDEST_NIGHT,
  PIP_AND_THE_VERY_SMALL_VISITOR,
];

export function getStory(idOrSlug: string): Story | undefined {
  return STORIES.find((s) => s.id === idOrSlug || s.slug === idOrSlug);
}

export function getTemplate(storyId: string, templateId: string) {
  return getStory(storyId)?.currentVersion.templates.find((t) => t.id === templateId);
}

/**
 * Resolves a template from an artwork's stored ids, whichever kind it is.
 *
 * Blank papers are not owned by a story, so they are looked up first and by id
 * alone. Everything downstream — the studio, thumbnails, exports — goes through
 * here, so free drawing needed no special case in any of them.
 */
export function resolveTemplate(
  storyId: string | null | undefined,
  templateId: string | null | undefined,
): ColoringTemplate | undefined {
  if (!templateId) return undefined;
  if (isPaper(templateId)) return getPaper(templateId);
  if (!storyId) return undefined;
  return getTemplate(storyId, templateId);
}
